import React from 'react';
import { createTheme } from '@mui/material/styles';
import { VpIcon } from './vp-ui';

const vpColors = {
  blueLighter: '#EDF4FE',
  blueBackgroundLight: '#DEEBFE',
  blueLight: '#B1D0FC',
  blueMain: '#2178F6',
  blueDark: '#1969D4',
  blueDarker: '#1351C8',
  redMain: '#DC646A',
  redError: '#F81414',
  greenMain: '#4CAF50',
  yellowMain: '#FFB300',
  white: '#FFFFFF',
  greyLighter: '#F4F4F4',
  greyLight: '#E5E5E5',
  greyLightTransparent: 'rgba(229, 229, 229, 0.5)',
  greyDarkTransparent: 'rgba(0, 0, 0, 0.06)',
  greyActive: '#CECECE',
  grey: '#AEAEAE',
  greyDark: '#525252',
  black: '#000000'
};

const vpFonts = {
  roboto: '"Roboto", "Helvetica", "Arial", sans-serif !important',
  inter: '"Inter", sans-serif !important'
};

export const theme = createTheme({
  spacing: 5,
  vpValues: {
    headerHeight: 62,
    appBarHeight: 52
  },
  palette: {
    primary: {
      light: vpColors.blueLight,
      main: vpColors.blueMain,
      dark: vpColors.blueDark
    },
    error: {
      main: vpColors.redMain
    },
    neutral: {
      main: vpColors.greyLighter
    },
    text: {
      primary: vpColors.greyDark
    },
    success: {
      main: vpColors.greenMain
    },
    warning: {
      main: vpColors.yellowMain
    },
    // add all the custom vp colors/fonts to the mui theme for reference when styling
    vpColors,
    vpFonts
  },
  typography: {
    fontFamily: vpFonts.roboto,
    h3: {
      fontWeight: 700,
      fontSize: 24
    },
    h5: {
      color: vpColors.greyDark
    },
    h6: {
      fontSize: 18,
      color: vpColors.black,
      fontWeight: 500
    },
    body1: {
      color: vpColors.greyDark,
      fontSize: '16px'
    },
    body2: {
      color: vpColors.greyDark,
      fontSize: '14px'
    },
    caption: {
      fontSize: '12px',
      color: vpColors.greyDark
    },
    dropDownText: {
      fontSize: '12px',
      color: vpColors.greyDark,
      maxWidth: '150px'
    },
    boldCaption: {
      fontWeight: 700,
      fontSize: '0.75em',
      color: vpColors.greyDark,
      textTransform: 'uppercase'
    },
    subtitle2: {
      color: vpColors.grey
    },
    inputLabel: {
      color: vpColors.black,
      fontWeight: 500,
      fontSize: '16px',
      fontFamily: vpFonts.roboto,
      '& svg': {
        position: 'relative',
        top: 2,
        left: 2,
        width: 16,
        height: 16
      }
    },
    inputLabelSmall: {
      color: vpColors.greyDark,
      fontSize: '12px',
      marginBottom: '5px',
      fontWeight: 500
    },
    checkboxLabel: {
      color: vpColors.greyDark,
      fontSize: '14px',
      fontWeight: 400
    },
    switchLabel: {
      color: vpColors.greyDark,
      fontSize: '12px',
      fontWeight: 500
    },
    formSectionTitle: {
      fontSize: '18px',
      color: vpColors.black,
      fontWeight: 900,
      fontFamily: vpFonts.inter
    },
    inputAdornment: {
      color: vpColors.grey,
      fontSize: '14px'
    },
    darkBold14: {
      fontSize: '14px',
      color: vpColors.black,
      fontWeight: 500,
      fontFamily: vpFonts.roboto
    },
    greyDark12Bold: {
      fontSize: '12px',
      color: vpColors.greyDark,
      fontWeight: 500,
      fontFamily: vpFonts.roboto
    },
    pageTitle: {
      fontSize: '18px',
      color: vpColors.black,
      fontWeight: 500,
      lineHeight: '16px'
    },
    pageSubTitle: {
      fontSize: '14px',
      color: vpColors.greyDark,
      lineHeight: '16px'
    },
    dialogBody2: {
      color: vpColors.greyDark,
      fontSize: '14px',
      padding: '0 60px',
      textAlign: 'center'
    },
    pluginTypeLabel: {
      fontWeight: 500,
      fontSize: '14px',
      fontFamily: vpFonts.inter,
      color: vpColors.greyDark
    },
    error: {
      color: vpColors.redError,
      fontSize: '14px'
    }
  },
  components: {
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true
      },
      styleOverrides: {
        // disabled: {
        //   cursor: 'default'
        // },
        root: ({ ownerState }) => ({
          textTransform: 'none',
          ...(ownerState.size === 'large' && {
            height: '44px',
            fontSize: '16px'
          }),
          ...(ownerState.size === 'medium' && {
            fontSize: '14px'
          }),
          ...(ownerState.inline && {
            height: 'auto',
            padding: 0
          }),
          ...(ownerState.disableBackground && {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }),
          ...(ownerState.underline && {
            textDecoration: 'underline',
            ':hover': {
              textDecoration: 'underline'
            }
          })
        }),
        contained: ({ ownerState }) => ({
          ...(ownerState.color === 'neutral' && {
            color: vpColors.greyDark,
            backgroundColor: vpColors.greyLighter,
            ':hover': {
              backgroundColor: vpColors.greyLight
            },
            ':active': {
              backgroundColor: vpColors.greyActive
            },
            ':disabled': {
              color: vpColors.grey,
              '.MuiButton-startIcon': {
                '& span': {
                  color: vpColors.grey
                }
              }
            },
            '.MuiButton-startIcon': {
              '& span': {
                color: vpColors.greyDark
              }
            }
          }),
          ...(ownerState.color === 'info' && {
            color: vpColors.blueMain,
            backgroundColor: vpColors.blueBackgroundLight,
            ':hover': {
              // TODO get these colors figured out when derek is back
              backgroundColor: vpColors.blueLighter
            },
            ':active': {
              backgroundColor: vpColors.blueBackgroundLight
            },
            ':disabled': {
              color: vpColors.grey
            }
          })
        }),
        outlined: ({ ownerState }) => ({
          ...(ownerState.color === 'neutral' && {
            color: vpColors.greyDark,
            borderColor: vpColors.greyLight,
            ':hover': {
              borderColor: vpColors.greyActive
            },
            ':active': {
              borderColor: vpColors.grey
            }
          })
        }),
        text: ({ ownerState }) => ({
          ...(ownerState.color === 'neutral' && {
            color: vpColors.greyDark,
            ':hover': {
              backgroundColor: vpColors.greyLighter
            },
            ':active': {
              backgroundColor: vpColors.greyLight
            },
            ':disabled': {
              color: vpColors.grey
            }
          })
        }),
        textInter: ({ ownerState }) => ({
          fontFamily: vpFonts.inter,
          ...(ownerState.color === 'neutral' && {
            color: vpColors.greyDark,
            ':hover': {
              backgroundColor: vpColors.greyLighter
            },
            ':active': {
              backgroundColor: vpColors.greyLight
            },
            ':disabled': {
              color: vpColors.grey
            }
          })
        }),
        navitem: ({ ownerState }) => ({
          fontSize: '14px',
          padding: '6px 16px',
          ...(ownerState.current && {
            backgroundColor: `${vpColors.blueLighter}`,
            color: `${vpColors.blueMain}`,
            ':hover': {
              backgroundColor: vpColors.blueBackgroundLight,
              color: `${vpColors.blueMain} !important`
            },
            ':active': {
              backgroundColor: vpColors.blueBackgroundLight,
              color: `${vpColors.blueMain} !important`
            },
            ':disabled': {
              color: vpColors.grey
            }
          }),
          ...(!ownerState.current && {
            backgroundColor: vpColors.white,
            color: vpColors.greyDark,
            ':hover': {
              backgroundColor: vpColors.greyLighter,
              color: `${vpColors.greyDark} !important`
            },
            ':active': {
              backgroundColor: vpColors.greyLighter,
              color: `${vpColors.greyDark} !important`
            },
            ':disabled': {
              color: vpColors.grey
            }
          })
        }),
        containedPrimary: {
          ':disabled': {
            background: vpColors.blueLight,
            color: vpColors.white
          },
          ':active': {
            background: vpColors.blueDarker
          },
          '.MuiButton-startIcon': {
            '& span': {
              color: vpColors.white
            }
          }
        },
        redButton: {
          fontSize: '14px',
          padding: '6px 16px',
          backgroundColor: 'red',
          color: vpColors.white,
          ':hover': {
            backgroundColor: `red`,
            color: `${vpColors.white} !important`
          },
          ':active': {
            backgroundColor: `red`,
            color: `${vpColors.white} !important`
          },
          ':disabled': {
            color: vpColors.grey
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
          fontSize: '12px'
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontFamily: vpFonts.roboto,
          '&::placeholder': {
            color: vpColors.grey,
            opacity: 1
          }
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${vpColors.greyLight}`
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `20px !important`
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          color: vpColors.blueMain,
          backgroundColor: vpColors.blueBackgroundLight
        }
      }
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave'
      },
      styleOverrides: {
        root: {
          backgroundColor: vpColors.greyLightTransparent
        }
      }
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
        deleteIcon: <VpIcon icon="X" />
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: vpColors.greyDarkTransparent, //vpColors.greyLighter
          ...(ownerState.color === 'success' && {
            color: vpColors.white,
            backgroundColor: vpColors.greenMain
          }),
          ...(ownerState.color === 'warning' && {
            color: vpColors.white,
            backgroundColor: vpColors.yellowMain
          }),
          ...(ownerState.color === 'info' && {
            color: vpColors.white,
            backgroundColor: vpColors.blueMain
          }),
          borderRadius: '5px',
          margin: '9px 0px'
        }),
        deleteIcon: {
          color: 'inherit'
        },
        colorTag: {
          color: vpColors.blueMain,
          backgroundColor: vpColors.blueLighter,
          fontSize: '12px',
          margin: '0'
        },
        primaryButton: {
          backgroundColor: vpColors.blueMain,
          color: vpColors.white,
          margin: 0,
          fontSize: '12px',
          padding: '5px 10px',
          height: '30px',
          fontFamily: vpFonts.inter,
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: vpColors.white,
            marginRight: 0
          }
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {}
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: vpColors.greyLighter
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          input: {
            backgroundColor: vpColors.white
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: vpColors.white,
          '& fieldset': {
            borderColor: vpColors.greyLight
          },
          '&.Mui-disabled': {
            backgroundColor: vpColors.greyLighter,
            '& textarea': {
              WebkitTextFillColor: vpColors.grey
            },
            '& input': {
              WebkitTextFillColor: vpColors.grey
            },
            '&.MuiInputBase-root': {
              '& fieldset': {
                borderColor: vpColors.greyLight
              }
            }
          }
        }
      }
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& svg': {
            width: '16px',
            height: '16px'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ ownerState }) => ({
          ...(ownerState.padding !== 'checkbox' && {
            paddingTop: '8px',
            paddingBottom: '8px'
          })
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          '& .MuiTooltip-tooltip': {
            margin: '8px !important',
            fontSize: '0.85em'
          }
        }
      }
    },
    MuiPagination: {
      styleOverrides: {
        ul: {
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: vpColors.blueLighter,
            color: vpColors.blueMain,
            '&:hover': {
              backgroundColor: vpColors.blueLight
            }
          }
        }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: vpColors.greyDark
        }
      }
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: vpColors.greyDark,
          ...(ownerState.color === 'neutral' && {
            backgroundColor: vpColors.greyLighter
          })
        })
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: vpColors.white
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            border: `1px solid ${vpColors.greyLight}`,
            boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.2)',
            '& .MuiList-root': {
              '& .MuiMenuItem-root': {
                padding: '8px 14px',
                '& .MuiTypography-root': {
                  fontSize: '14px'
                },
                '& .MuiSvgIcon-root': {
                  width: '16px',
                  height: '16px'
                }
              }
            }
          }
        }
      }
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 34,
          height: 20,
          padding: 0,
          display: 'flex',
          '&:active': {
            '& .MuiSwitch-thumb': {
              width: 15
            },
            '& .MuiSwitch-switchBase.Mui-checked': {
              transform: 'translateX(10px)'
            }
          },
          '& .MuiSwitch-switchBase': {
            padding: 3,
            '&.Mui-disabled': {
              color: vpColors.white,
              '& + .MuiSwitch-track': {
                opacity: 1
              }
            },
            '&.Mui-checked': {
              transform: 'translateX(14px)',
              color: vpColors.white,
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: vpColors.blueMain
              },
              '&.Mui-disabled': {
                color: vpColors.white,
                '& + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: vpColors.blueBackgroundLight
                }
              }
            }
          },
          '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
            width: 14,
            height: 14,
            borderRadius: 100,
            transition: theme.transitions.create(['width'], {
              duration: 200
            })
          },
          '& .MuiSwitch-track': {
            borderRadius: 100,
            opacity: 1,
            backgroundColor: vpColors.greyLight,
            boxSizing: 'border-box'
          }
        })
      }
    }
  }
});



// WEBPACK FOOTER //
// ./src/theme.js